import { CheckIcon } from "@heroicons/react/24/outline";
import { Button, Card, CardBody, Spinner } from "@material-tailwind/react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { Fragment, useDeferredValue, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useUserStore } from "../../Context/store";
import { useSubworkspaceStore } from "../../Context/store";
import { useWorkspaceStore } from "../../Context/store";
import InputField from "../../Layout/InputField";
import { getInitials } from "../../Utils/Helper";
import { useValidateSpaceName } from "../../Web-Hooks/use-validation";
import {
  useDeleteUserFromSubWorkspace,
  useRemoveUserFromSubWorkspace,
  useUpdateSubWorkspace,
} from "../../Web-Hooks/Workspace/use-subworkspace";
import {
  useDeleteUserFromWorkspace,
  useRemoveUserFromWorkspace,
  useUpdateWorkspace,
} from "../../Web-Hooks/Workspace/use-workspace";
import {
  subWorkspaceDeleteLongText,
  subWorkspaceDeleteTitle,
  subWorkspaceLeaveForum,
  subWorkspaceLeaveLongText,
  workspaceDeleteLongText,
  workspaceDeleteTitle,
  workspaceLeaveForum,
  workspaceLeaveLongText,
} from "../../Utils/Constant";

const AboutDebate = ({ popupFor, handleClose }) => {
  const navigate = useNavigate();
  const { selectedWorkspace, selectWorkspace } = useWorkspaceStore();
  const { selectedSubWorkspace, selectSubWorkspace, subWorkspaceList } =
    useSubworkspaceStore();
  const { user } = useUserStore();
  const Details =
    popupFor === "subworkspace" ? selectedSubWorkspace : selectedWorkspace;
  const defaultName =
    popupFor === "subworkspace"
      ? selectedSubWorkspace?.subWorkSpaceName
      : selectedWorkspace?.workSpaceName;
  const [name, setName] = useState(defaultName || "");
  const [edit, setEdit] = useState(false);
  const deferredName = useDeferredValue(name);
  const queryClient = useQueryClient();
  const type = popupFor === "subworkspace" ? "SubWork" : "Work";
  const id = popupFor === "subworkspace" ? selectedWorkspace?.workSpaceId : "";
  const { data: isValidWorkspaceName, isLoading: isValidatingWorkspaceName } =
    useValidateSpaceName(type, deferredName || null, id);
  const { mutateAsync: updateWorkspace } = useUpdateWorkspace();
  const { mutateAsync: updateSubWorkspace } = useUpdateSubWorkspace();
  const { mutateAsync: leaveFromSubworkspace } =
    useRemoveUserFromSubWorkspace();
  const { mutateAsync: leaveFromWorkspace } = useRemoveUserFromWorkspace();
  const { mutateAsync: deleteSubworkspace } = useDeleteUserFromSubWorkspace();
  const { mutateAsync: deleteWorkspace } = useDeleteUserFromWorkspace();

  const isLoggedInUserIsAdmin = [Details?.createdBy]?.find(
    (creator) => creator?.userId === user?.userId
  )
    ? true
    : false;

  const message = isLoggedInUserIsAdmin ? "delete" : "leave";

  const setWorkspaceName = (e) => {
    const { name, value } = e.target;
    // Replace spaces with dashes only if the input name is "WorkSpaceName"
    const processedValue =
      name === "WorkSpaceName" ? value.replace(/\s+/g, "-") : value;
    setName(processedValue);
  };

  const handleChange = (workspace, value) => {
    if (value === "") return;
    if (popupFor === "subworkspace") {
      const updatedSubWorkspace = {
        WorkSpaceId: workspace.workSpaceId,
        SubWorkSpaceId: workspace.subWorkSpaceId,
        InvitedBy: user?.userId,
        SubWorkSpaceName: value,
      };
      value !== "" &&
        value !== selectedSubWorkspace?.subWorkSpaceName &&
        updateSubWorkspace(updatedSubWorkspace)
          .then((res) => {
            selectSubWorkspace({
              ...selectedSubWorkspace,
              subWorkSpaceName: value,
            });
            // UpdateSelectedSubWorkspace(subworkSpace);
          })
          .finally(() => {
            queryClient.invalidateQueries({
              queryKey: ["sub-workspaces", selectedWorkspace?.workSpaceId],
            });
          });
    } else {
      const updatedWorkspace = {
        WorkSpaceId: workspace.workSpaceId,
        WorkSpaceName: value,
        WorkSpaceInitials: getInitials(value),
        UserId: user.userId,
      };
      value !== "" &&
        value !== selectedWorkspace?.workSpaceName &&
        updateWorkspace(updatedWorkspace)
          .then((res) => {
            selectWorkspace({ ...selectedWorkspace, workSpaceName: value });

            // UpdateSelectedSubWorkspace(workSpace);
          })
          .finally(() => {
            queryClient.invalidateQueries({
              queryKey: ["workspaces", user?.userId],
            });
          });
    }

    setEdit(false);
  };

  const handleLeave = async () => {
    const isLastForum = subWorkspaceList?.length === 1;
    const titleLastForum = isLoggedInUserIsAdmin
      ? workspaceDeleteTitle
      : workspaceLeaveForum;
    const titleForum = isLoggedInUserIsAdmin
      ? subWorkspaceDeleteTitle
      : subWorkspaceLeaveForum;
    const descriptionLastForum = isLoggedInUserIsAdmin
      ? workspaceDeleteLongText
      : workspaceLeaveLongText;
    const descriptionForum = isLoggedInUserIsAdmin
      ? subWorkspaceDeleteLongText
      : subWorkspaceLeaveLongText;
    Swal.fire({
      title: isLastForum ? titleLastForum : titleForum,
      text: isLastForum ? descriptionLastForum : descriptionForum,
      customClass: {
        title: "p-0 !my-2 text-2xl font-bold text-debatePalette-title",
        image: "p-0 m-0 w-full mx-auto",
        popup: "flex flex-col items-center gap-3 ",
        htmlContainer: " !py-0 !px-16 !text-justify !text-lg",
        actions: "m-0",
        container: "!z-[99999]",
        cancelButton: "!border border-debatePalette-buttonBorder text-black bg-transparent",
        confirmButton: "bg-primary text-black"
      },
      showLoaderOnConfirm: true,
      showCancelButton: true,
      confirmButtonText: "Yes, " + message + " it!",
      preConfirm: async () => {
        const deleteUser = {
          [popupFor === "subworkspace" ? isLastForum ? "WorkSpaceId" : "SubWorkSpaceId" : "WorkSpaceId"]:
            popupFor === "subworkspace"
              ? isLastForum ? selectedWorkspace?.workSpaceId : selectedSubWorkspace?.subWorkSpaceId
              : selectedWorkspace?.workSpaceId,
          LoginUserId: user?.userId,
          UserId: user?.userId,
        };

        try {
          const res = await (popupFor === "subworkspace"
            ? isLoggedInUserIsAdmin
              ? isLastForum ? deleteWorkspace : deleteSubworkspace
              : isLastForum ? leaveFromWorkspace : leaveFromSubworkspace
            : isLoggedInUserIsAdmin
            ? deleteWorkspace
            : leaveFromWorkspace)(deleteUser);

          if (res.status !== 200) {
            toast?.error(res?.data?.error?.message);
            return;
          }

          toast.success(
            `You have successfully ${
              isLoggedInUserIsAdmin ? "deleted" : "left"
            } ${defaultName}`
          );
          if(isLastForum){
            selectWorkspace(null);
            selectSubWorkspace(null);
            navigate("/createworkspace");
            return;
          }

          if (popupFor === "subworkspace") {
            const anotherSubworkspaceExist = subWorkspaceList.find(
              (subWorkspace) =>
                subWorkspace.subWorkSpaceId !==
                selectedSubWorkspace?.subWorkSpaceId
            );
            selectSubWorkspace(anotherSubworkspaceExist);
            handleClose();
            navigate("/feed-public", { replace: true });
          } else {
            navigate("/createworkspace");
            return;
          }
        } catch (err) {
          toast.error(err.message);
        }
      },
    });
  };

  return (
    <Fragment>
      <Card shadow={false} className="">
        <CardBody className="p-0 border border-debatePalette-buttonBorder rounded-lg ">
          <div className="grid grid-cols-[1fr_auto] min-w-full items-center gap-2 border-b px-2 border-debatePalette-buttonBorder py-4">
            <div className="line-clamp-1 break-words">
              {edit ? (
                <InputField
                  isControlled={true}
                  inputName="WorkSpaceName"
                  value={name}
                  onChange={setWorkspaceName}
                  icon={
                    isValidatingWorkspaceName ? (
                      <Spinner />
                    ) : (
                      isValidWorkspaceName && (
                        <CheckIcon className="font-bold" />
                      )
                    )
                  }
                  isError={
                    isValidatingWorkspaceName
                      ? false
                      : name !== defaultName &&
                        !isValidWorkspaceName &&
                        name !== ""
                  }
                  // errorMessage={
                  //   isValidatingWorkspaceName
                  //     ? ""
                  //     : !isValidWorkspaceName &&
                  //       name !== "" &&
                  //       "Debatebase name already exists"
                  // }
                />
              ) : (
                <p className="text-sm font-semibold text-debatePalette-title capitalize">
                  {defaultName}
                </p>
              )}
            </div>
            {(
              popupFor === "subworkspace"
                ? user?.role === "Admin" || user?.role === "Manager" || user?.role === "SubAdmin"
                : user?.role === "Admin" || user?.role === "SubAdmin"
            ) ? (
              <p
                className="text-sm font-medium shrink-0  text-debatePalette-shadow capitalize cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  !edit
                    ? setEdit(true)
                    : handleChange(selectedSubWorkspace, deferredName);
                }}
              >
                {edit
                  ? !isValidWorkspaceName && deferredName !== defaultName
                    ? ""
                    : "Save"
                  : "Edit"}
              </p>
            ) : null}
          </div>

          {popupFor === "subworkspace" ? (
            <div className="flex justify-between px-2 border-b w-full border-debatePalette-buttonBorder py-4">
              <div className="flex flex-col w-full">
                <p className="text-sm font-bold text-debatePalette-title capitalize">
                  Managed By
                </p>

                <div className="text-sm font-medium text-debatePalette-shadow max-h-16 w-full capitalize overflow-y-auto">
                  {Details?.managedBy?.map((user) => {
                    return (
                      <div className="flex gap-2" key={user?.userName}>
                        <p className="text-sm text-debatePalette-bodyText capitalize">
                          {user?.userName}
                        </p>
                        <span className="text-sm text-debatePalette-shadow capitalize">
                          {user?.role}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex justify-between px-2  border-debatePalette-buttonBorder py-4">
            <div className="flex flex-col">
              <p className="text-sm font-bold text-debatePalette-title capitalize">
                Created By
              </p>

              <div className="text-xs font-medium text-debatePalette-bodyText capitalize">
                {[Details?.createdBy]?.map((user) => {
                  return (
                    <div className="flex gap-2" key={user?.userName}>
                      <p className="text-sm text-debatePalette-bodyText">
                        {user?.userName}{" "}
                      </p>
                      <span className="text-sm text-debatePalette-bodyText ">
                        on {moment(user?.createdDate).format("MMM DD, YYYY")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {popupFor === "subworkspace" && (
            <div className="grid grid-cols-[auto_1fr] gap-3 w-full px-2 justify-between py-4">
              <div className=" break-words line-clamp-1">
                <Button
                  variant="text"
                  className="text-sm font-bold p-1 text-debatePalette-timer capitalize cursor-pointer"
                  onClick={() => handleLeave()}
                >
                  {isLoggedInUserIsAdmin ? "Delete" : "Leave"}
                </Button>
              </div>
              {/* <div className=" break-words line-clamp-1">
                <p
                  className="text-sm font-bold text-debatePalette-timer capitalize cursor-pointer"
                  onClick={() => handleLeave()}
                >
                  {defaultName}
                </p>
              </div> */}
            </div>
          )}
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default AboutDebate;
